import React from 'react'
import remarkGfm from 'remark-gfm'

export interface MdastNode {
    children?: MdastNode[]
    data?: {hName?: string}
    type: string
    value?: string
    depth?: number
    position?: any
}

// Markdown parses <small> and </small> as separate inline `html` nodes with the
// content in between. This plugin finds that open/close pair among siblings and
// wraps the nodes between them in a <small> element, so authors can produce
// small text without enabling raw HTML in react-markdown.
const OPEN_TAG = /^<small(?:\s[^>]*)?>$/iu
const CLOSE_TAG = /^<\/small\s*>$/iu

const isOpenTag = (node: MdastNode): boolean => node.type === 'html' && OPEN_TAG.test((node.value ?? '').trim())

const isCloseTag = (node: MdastNode): boolean => node.type === 'html' && CLOSE_TAG.test((node.value ?? '').trim())

const findCloseIndex = (children: MdastNode[], openIndex: number): number => {
    let depth = 0

    for (let index = openIndex; index < children.length; index += 1) {
        if (isOpenTag(children[index])) {
            depth += 1
        } else if (isCloseTag(children[index])) {
            depth -= 1

            if (depth === 0) {
                return index
            }
        }
    }

    return -1
}

export const remarkSmall = () => (tree: MdastNode): void => {
    const visit = (node: MdastNode): void => {
        if (!Array.isArray(node.children)) {
            return
        }

        const nextChildren: MdastNode[] = []
        let index = 0

        while (index < node.children.length) {
            const child = node.children[index]

            if (isOpenTag(child)) {
                const closeIndex = findCloseIndex(node.children, index)

                if (closeIndex === -1) {
                    visit(child)
                    nextChildren.push(child)
                    index += 1
                } else {
                    const smallNode: MdastNode = {
                        children: node.children.slice(index + 1, closeIndex),
                        data: {hName: 'small'},
                        type: 'small',
                    }
                    visit(smallNode)
                    nextChildren.push(smallNode)
                    index = closeIndex + 1
                }
            } else {
                visit(child)
                nextChildren.push(child)
                index += 1
            }
        }

        node.children = nextChildren
    }

    visit(tree)
}

export const markdownProps = {
    components: {
        a: (props: React.HTMLProps<HTMLAnchorElement>): React.ReactElement => {
            const customTarget = (props.title || '').includes('target:_top') ? '_top' : '_blank'
            return <a {...props} rel='noopener noreferrer' target={customTarget} />
        },
    },
    remarkPlugins: [remarkGfm, remarkSmall],

}
